/** @jsxRuntime automatic */
/** @jsxImportSource react */
/**
 * Fleet health › Overview (docs/CONTRACT-fleet-governance.md section 12.2): ten tiles (the filter tiles link to the PCs view
 * filtered), version adoption grouped by ring with the 30-day share on the current version, the thirteen problem kinds
 * with their counts, the local connections across the fleet, and the newest ten critical entries, from
 * GET /v1/admin/fleet/overview with the ring filter.
 */
import { useState } from 'react';
import { Cable, CircleCheck, Siren } from 'lucide-react';
import {
  FLEET_PROBLEM_KINDS, PROBLEM_LABELS, RINGS, fleetApi, fleetFailure, mcpName, pctText, ringLabel, sourcesFailedText,
  type FleetMcpSummary, type FleetOverview as OverviewData, type FleetProblemKind, type Ring,
} from '../../lib/fleet';
import { EmptyState, Kpi, PageHeader, Panel, Select, ShareBar, Sparkline, Stack, Table, Tag, type Column } from '../../ui';
import { count, dateTime } from '../../ui/format';
import { useLoad } from '../pulse/common';
import { LoadError, PageLink, SourcesNote, type FleetFrame } from './parts';

interface AdoptionRow { key: string; ring: Ring | 'unknown'; current: string | null; version: string; pcs: number; sharePct: number; isCurrent: boolean }
type CriticalRow = OverviewData['latestCritical'][number];
type ProblemCount = { kind: FleetProblemKind; pcs: number };

const pcsHref = (filter: Record<string, string>) => `/fleet?${new URLSearchParams({ view: 'pcs', ...filter }).toString()}`;
const RING_ORDER: ReadonlyArray<Ring | 'unknown'> = [...RINGS, 'unknown'];

function adoptionRows(data: OverviewData): AdoptionRow[] {
  const groups = [...data.adoption].sort((a, b) => RING_ORDER.indexOf(a.ring) - RING_ORDER.indexOf(b.ring));
  return groups.flatMap((group) => group.versions.map((version) => ({ key: `${group.ring}|${version.version}`, ring: group.ring, current: group.current, ...version })));
}

/** The thirteen kinds in the closed order, a kind the answer left out counted as 0. */
function problemCounts(data: OverviewData): ProblemCount[] {
  return FLEET_PROBLEM_KINDS.map((kind) => ({ kind, pcs: data.problems.find((row) => row.kind === kind)?.pcs ?? 0 }));
}

export function FleetOverview({ frame }: { frame: FleetFrame }) {
  const [ring, setRing] = useState('');
  const { load, reload, reloading } = useLoad(() => fleetApi.overview(ring || undefined), `overview|${ring}`);
  const data = load.state === 'ready' ? load.data : null;
  const loading = load.state === 'loading';
  const tiles = data?.tiles;
  const show = (filter: Record<string, string>) => <PageLink href={pcsHref(filter)}>Show these PCs</PageLink>;

  const adoptionColumns: Array<Column<AdoptionRow>> = [
    { key: 'version', header: 'Version', mono: true, cell: (row) => (row.isCurrent ? `${row.version} (current)` : row.version) },
    { key: 'share', header: 'Share of the ring', cell: (row) => <ShareBar value={row.sharePct / 100} label={`${row.version}: ${pctText(row.sharePct)} of the ${ringLabel(row.ring)} ring`} /> },
    { key: 'pcs', header: 'PCs', numeric: true, cell: (row) => count(row.pcs) },
  ];
  const problemColumns: Array<Column<ProblemCount>> = [
    { key: 'kind', header: 'Problem kind', cell: (row) => <PageLink href={pcsHref({ problem: row.kind })}>{PROBLEM_LABELS[row.kind]}</PageLink> },
    { key: 'pcs', header: 'PCs', numeric: true, cell: (row) => (row.pcs === 0 ? <span className="ui-quiet">0</span> : count(row.pcs)) },
  ];
  const mcpColumns: Array<Column<FleetMcpSummary>> = [
    { key: 'server', header: 'Local connection', cell: (row) => <span title={row.server}>{mcpName(row)}</span> },
    { key: 'pcs', header: 'PCs reporting', numeric: true, cell: (row) => count(row.pcs) },
    { key: 'healthy', header: 'Healthy', numeric: true, cell: (row) => count(row.healthy) },
    { key: 'unhealthy', header: 'Unhealthy', numeric: true, cell: (row) => (row.unhealthy > 0 ? <span className="ui-text-bad">{count(row.unhealthy)}</span> : count(row.unhealthy)) },
    { key: 'other', header: 'Other', numeric: true, hideOnPhone: true, cell: (row) => count(row.other) },
    { key: 'topErrorCode', header: 'Top error code', mono: true, hideOnPhone: true, cell: (row) => row.topErrorCode ?? '—' },
  ];
  const criticalColumns: Array<Column<CriticalRow>> = [
    { key: 'at', header: 'Time', muted: true, cell: (row) => dateTime(row.atMs) },
    { key: 'pc', header: 'PC', cell: (row) => <PageLink href={`/fleet?${new URLSearchParams({ view: 'overview', pc: row.deviceId }).toString()}`} title={row.deviceId}>{row.computerName ?? row.deviceId}</PageLink> },
    { key: 'category', header: 'Category', hideOnPhone: true, cell: (row) => <Tag>{row.category}</Tag> },
    { key: 'message', header: 'Message', cell: (row) => <span className="ui-clip" title={row.message}>{row.message}</span> },
  ];

  return (
    <>
      <PageHeader
        tabs={frame.tabs}
        activeTab={frame.activeTab}
        meta={<span>Every PC with Gyro: its version, when it was last seen and what is failing on it.</span>}
        actions={(
          <>
            <Select size="sm" className="ui-w-auto" aria-label="Ring" value={ring} onChange={(event) => setRing(event.target.value)}>
              <option value="">Ring: All</option>
              {RINGS.map((value) => <option key={value} value={value}>{ringLabel(value)}</option>)}
            </Select>
            {frame.actions}
          </>
        )}
      />
      <div className="ui-page">
        <Stack>
          {load.state === 'failed' ? <LoadError title="The Fleet overview could not be loaded" error={load.error} words={(error) => fleetFailure(error)} onRetry={reload} retrying={reloading} /> : null}
          <SourcesNote text={sourcesFailedText(data?.sourcesFailed)} />
          <div className="pulse-kpis" role="group" aria-label="Fleet at a glance">
            <Kpi label="PCs enrolled" loading={loading} value={tiles ? count(tiles.enrolled) : '—'} sub="with at least one active key" />
            <Kpi label="Seen in 24 hours" loading={loading} value={tiles ? count(tiles.seen24h) : '—'} />
            <Kpi label="Not seen 72 hours or more" loading={loading} value={tiles ? count(tiles.notSeen72h) : '—'} sub={show({ status: 'not-seen' })} />
            <Kpi label="On the ring's current version" loading={loading} value={tiles ? pctText(tiles.onCurrentPct) : '—'} sub={tiles && tiles.onCurrentPct === null ? 'no ring has a current version yet' : undefined} />
            <Kpi label="Crashes in 7 days" loading={loading} value={tiles ? count(tiles.crashes7d) : '—'} />
            <Kpi label="WebView2 failures in 7 days" loading={loading} value={tiles ? count(tiles.webview2Failures7d) : '—'} />
            <Kpi label="Sign-in failures in 7 days" loading={loading} value={tiles ? count(tiles.signInFailures7d) : '—'} />
            <Kpi label="MCP switches off" loading={loading} value={tiles ? count(tiles.mcpOff) : '—'} sub={show({ problem: 'mcp-off' })} />
            <Kpi label="Policy behind" loading={loading} value={tiles ? count(tiles.policyBehind) : '—'} sub={show({ problem: 'policy-behind' })} />
            <Kpi label="Connected now" loading={loading} value={tiles ? count(tiles.connectedNow) : '—'} sub={show({ status: 'online' })} />
          </div>

          <Panel
            flush
            title="Version adoption"
            description="Each ring's versions, with the share of the ring's PCs on each"
            actions={data ? (
              <span className="ui-chips">
                <span className="ui-quiet">Share on the ring's current version, 30 days</span>
                <Sparkline values={data.adoptionSpark.map((value) => value ?? 0)} max={100} label="Share on the ring's current version, 30 days" />
              </span>
            ) : null}
          >
            <Table
              label="Version adoption"
              columns={adoptionColumns}
              rows={data ? adoptionRows(data) : []}
              rowKey={(row) => row.key}
              groupOf={(row) => `${ringLabel(row.ring)} · current ${row.current ?? 'unknown'}`}
              loading={loading}
              skeletonRows={4}
              empty={<EmptyState compact icon={CircleCheck} title="No versions reported yet">Each PC's version appears here once it reports its health, its switches or its logs.</EmptyState>}
            />
          </Panel>

          <Panel flush title="Problem PCs" description="PCs carrying each problem kind now; each opens the PCs view filtered to it">
            <Table label="Problem PCs" columns={problemColumns} rows={data ? problemCounts(data) : []} rowKey={(row) => row.kind} loading={loading} skeletonRows={6} />
          </Panel>

          <Panel flush title="Local connections" description="Per MCP server, over the PCs whose list is at most 24 hours old">
            <Table
              label="Local connections"
              columns={mcpColumns}
              rows={data?.mcpServers ?? []}
              rowKey={(row) => row.server}
              loading={loading}
              skeletonRows={3}
              empty={<EmptyState compact icon={Cable} title="No PC has reported its local connections yet">A PC reports them with its health report once it runs an agent version that sends them.</EmptyState>}
            />
          </Panel>

          <Panel flush title="Newest critical entries" description="The ten newest critical entries and crash dumps across the fleet">
            <Table
              label="Newest critical entries"
              columns={criticalColumns}
              rows={data?.latestCritical ?? []}
              rowKey={(row, index) => `${row.deviceId}|${row.atMs}|${index}`}
              loading={loading}
              skeletonRows={4}
              empty={<EmptyState compact icon={Siren} title="No critical entries">Critical entries and crash dumps from the PCs' logs appear here.</EmptyState>}
            />
          </Panel>
        </Stack>
      </div>
    </>
  );
}
